import { useState } from 'react';
import { Search } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Input } from '@/components/ui/input';
import type { MenuItem } from './sidebarConfig';

type MenuResult = { title: string; url: string; path: string };

const MAX_RESULTS = 8;

const normalize = (text: string): string =>
  text.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().trim();

/** Toutes les pages du menu visible, sans doublon d'adresse. */
function flattenMenu(items: readonly MenuItem[]): MenuResult[] {
  const results = new Map<string, MenuResult>();
  const add = (title: string, url: string, path: string) => {
    if (!results.has(url)) results.set(url, { title, url, path });
  };
  for (const item of items) {
    if (item.url) add(item.title, item.url, item.title);
    for (const child of item.children ?? []) add(child.title, child.url, item.title);
    for (const section of item.sections ?? []) {
      for (const child of section.children) {
        add(child.title, child.url, `${item.title} › ${section.title}`);
      }
    }
  }
  return [...results.values()];
}

/** Recherche sans accent ni casse sur le nom de la page et sa rubrique ; le nom passe d'abord. */
export function searchMenu(items: readonly MenuItem[], query: string): MenuResult[] {
  const words = normalize(query).split(/\s+/).filter(Boolean);
  if (words.length === 0) return [];
  const matches = flattenMenu(items).filter((entry) => {
    const text = normalize(`${entry.title} ${entry.path}`);
    return words.every((word) => text.includes(word));
  });
  const inTitle = (entry: MenuResult) => normalize(entry.title).includes(words[0]);
  return [...matches.filter(inTitle), ...matches.filter((entry) => !inTitle(entry))].slice(
    0,
    MAX_RESULTS,
  );
}

/** Champ de recherche du menu : ouvre directement la page choisie. */
export function MenuSearch({ menu }: { menu: readonly MenuItem[] }) {
  const [query, setQuery] = useState('');
  const navigate = useNavigate();
  const results = searchMenu(menu, query);
  const open = (url: string) => {
    setQuery('');
    navigate(url);
  };

  return (
    <div className="relative px-3 py-3">
      {query.trim() && (
        <div className="absolute inset-x-3 bottom-full z-50 max-h-72 overflow-y-auto rounded-md border border-sidebar-border bg-sidebar p-1 shadow-lg">
          {results.length === 0 && (
            <p className="px-3 py-2 text-[13px] text-sidebar-foreground/60">Aucune page trouvée</p>
          )}
          {results.map((result) => (
            <button
              key={result.url}
              type="button"
              onClick={() => open(result.url)}
              className="block w-full rounded px-3 py-2 text-left hover:bg-sidebar-accent"
            >
              <span className="block text-[13px] text-sidebar-foreground">{result.title}</span>
              <span className="block text-[11px] text-sidebar-foreground/50">{result.path}</span>
            </button>
          ))}
        </div>
      )}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-sidebar-foreground/40" />
        <Input
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter' && results[0]) open(results[0].url);
            if (event.key === 'Escape') setQuery('');
          }}
          placeholder="Rechercher une page..."
          className="h-9 border-sidebar-border bg-sidebar-accent pl-9 text-sm text-sidebar-foreground placeholder:text-sidebar-foreground/40"
        />
      </div>
    </div>
  );
}
